import {
	isConcludedStatus,
	isSettledStatus,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/Documents.utils';
import { AssignSignerForm } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/AssignSignerForm';
import { SignerCard } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/SignerCard';
import { SignerGroupHeader } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/SignerGroupHeader';
import {
	buildSignerMenuItems,
	canCancelAuthorisedSignatory,
	canToggleSimpleSign,
	canToggleWitness,
	isEnablingWitnessBlocked,
	collectGroupRoleIds,
	flattenGroupSigners,
	getClaimedSignGate,
	getDisplaySignerStatus,
	getIsSignerReadyToSign,
	getSignerActionGates,
	getWitnessControlsForSigner,
	isClaimableInvestorSigner,
	isClaimedByCurrentUser,
	witnessSignersSupported,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/SignerGroupSection.utils';
import type {
	SignerWithRepresentative,
	SignerWithRepresentativeGroup,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/SignerTab.utils';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import {
	DocumentSigningRoleType,
	DocumentSigningScope,
	DocumentSigningSecurityLevel,
	DocumentWorkflowInstanceSignatureStatus,
	DocumentWorkflowInstanceStatus,
	ESignProvider,
	InvestmentProgrammeDocumentDetailsSignerRes,
	InvestmentProgrammeDocumentDetailsWitnessRes,
	SignatureType,
	TemplateSettingLockRes,
} from '@dealstack/shared/lib/generated';
import { Flex } from '@mantine/core';
import type { UserOption } from './AssignSignerForm.types';
import { GroupStatusDisplay } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/SignerGroupHeader.types';
import { GroupMenuItem } from '@dealstack/frontend/shared/components/ExpandablePersonCard/GroupHeader.types';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { EmptySignerSlot } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/EmptySignerSlot';
import {
	canRemoveSigner,
	describeRemoval,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/RemoveSigner/removeSigner.utils';
import type { RemovalTarget } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/RemoveSigner/RemoveSignerModal.types';
import type { RemovalTargetSigner } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/RemoveSigner/removeSigner.utils.types';
import { useBulkSetSignerRequiresWitness } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/api/useBulkSetSignerRequiresWitness';
import { useBulkSetSignerSecurityLevel } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/api/useBulkSetSignerSecurityLevel';
import { useSetSignerRequiresWitness } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/api/useSetSignerRequiresWitness';
import { useSetSignerSecurityLevel } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/api/useSetSignerSecurityLevel';
import { useIsDealstackSupport } from '@dealstack/frontend/shared/hooks/useIsDealstackSupport';

interface Props {
	group: SignerWithRepresentativeGroup;
	documentSignatureType?: SignatureType | null;
	documentSigningScope?: DocumentSigningScope | null;
	documentESignProvider?: ESignProvider | null;
	currentUserId?: string | null;

	isSignerUpdatePending: boolean;

	onSignClick?: () => void;

	onAssignClick: (slotIndex: number) => void;
	onUnassignClick: () => void;

	assigningSlotIndex: number | null;
	isUnassignmentGroup: boolean;

	assignUserOptions: UserOption[];
	isLoadingUsers: boolean;
	isAssignmentPending: boolean;
	isUnassignmentPending: boolean;

	unassignFormLabels: {
		placeholder: string;
		confirmLabel: string;
		nothingFoundMessage: string;
	};

	eSignInitiated: boolean;
	isCounterpartySigningBlocked: boolean;
	/** This document signs through eID Easy (eSignProvider === EID_EASY). */
	isEidEasyDocument?: boolean;

	/** The document workflow is COMPLETED — hides the simple-sign / witness toggles. */
	documentFullySigned: boolean;

	/** The instance's own status, which gates whether a signature requirement can be removed. */
	workflowStatus: DocumentWorkflowInstanceStatus;

	/** Whether the API will still accept ENABLING a witness requirement on this document. */
	witnessRequirementLock?: TemplateSettingLockRes;

	onInviteWitness?: (parentSignatureId: string) => void;
	onInviteAuthorisedSignatory?: (signatureId: string) => void;
	onCancelAuthorisedSignatory?: (signatureId: string, signatoryName: string) => void;
	onResendWitnessInvitation?: (signatureId: string) => void;
	onResendAuthorisedSignatoryInvitation?: (signatureId: string) => void;
	resendingSignatureId?: string | null;

	onAssignConfirm: (userId: string) => void;
	onAssignCancel: () => void;
	onUnassignConfirm: (userId: string) => void;
	onUnassignCancel: () => void;

	status?: GroupStatusDisplay;
	currentUserCanClaim: boolean;
	onClaim: () => void;
	isClaimPending: boolean;
	claimEnabled?: boolean;
	onClaimSigner?: (signerId: string) => void;
	claimingSignerId?: string | null;
	onSignClaimedSigner?: (
		signer: SignerWithRepresentative,
		signingRoleType: DocumentSigningRoleType
	) => void;

	/** Signers on the WHOLE document — the backend refuses a removal that would empty it. */
	documentSignerCount: number;
	/** Investor (PARTY) signers on the WHOLE document — the backend keeps at least one. */
	documentPartyCount: number;
	/** Document-wide: the backend refuses a removal when nothing is left waiting on a signer. */
	hasOutstandingSignatures: boolean;
	/** Opens the shared removal confirmation modal owned by SignersTabContent. */
	onRemoveSigner: (target: RemovalTarget) => void;
}

// TODO this needs a refactor doing too much and is getting unwieldy, consider breaking out into smaller components and/or using a state machine to manage the various states and actions
export const SignerGroupSection = ({
	group,
	documentSignatureType,
	documentSigningScope,
	documentESignProvider,
	currentUserId,
	isSignerUpdatePending,
	onSignClick,
	onAssignClick,
	onUnassignClick,
	assigningSlotIndex,
	isUnassignmentGroup,
	assignUserOptions,
	isLoadingUsers,
	isAssignmentPending,
	isUnassignmentPending,
	unassignFormLabels,
	eSignInitiated,
	isCounterpartySigningBlocked,
	isEidEasyDocument,
	documentFullySigned,
	workflowStatus,
	witnessRequirementLock,
	onInviteWitness,
	onInviteAuthorisedSignatory,
	onCancelAuthorisedSignatory,
	onResendWitnessInvitation,
	onResendAuthorisedSignatoryInvitation,
	resendingSignatureId,
	onAssignConfirm,
	onAssignCancel,
	onUnassignConfirm,
	onUnassignCancel,
	status,
	currentUserCanClaim,
	onClaim,
	isClaimPending,
	claimEnabled = false,
	onClaimSigner,
	claimingSignerId,
	onSignClaimedSigner,
	documentSignerCount,
	documentPartyCount,
	hasOutstandingSignatures,
	onRemoveSigner,
}: Props) => {
	const tMipManage = useTranslationMipManage();

	const buildClaimMenuItems = (
		claimSigner: Pick<
			InvestmentProgrammeDocumentDetailsSignerRes,
			'id' | 'status' | 'claimedByName' | 'investorId'
		>
	): GroupMenuItem[] => {
		if (!claimEnabled) {
			return [];
		}
		const claimSignerId = claimSigner.id;
		if (!onClaimSigner || !claimSignerId || !isClaimableInvestorSigner(claimSigner)) {
			return [];
		}
		return [
			{
				key: 'claim-on-behalf',
				label: tMipManage('documents_page.signer.menu.claim_on_behalf'),
				onClick: () => {
					onClaimSigner(claimSignerId);
				},
				disabled: claimingSignerId === claimSignerId,
				testId: TEST_ELEMENT_IDS.DOCUMENT_SIGNER_CLAIM_ON_BEHALF,
			},
		];
	};

	const bulkSetWitnessMutation = useBulkSetSignerRequiresWitness();
	const bulkSetSecurityLevelMutation = useBulkSetSignerSecurityLevel();
	const setSignerWitnessMutation = useSetSignerRequiresWitness();
	const setSignerSecurityLevelMutation = useSetSignerSecurityLevel();

	const isWitnessGroup = group.groupType === DocumentSigningRoleType.WITNESS;
	const isDealstackSupport = useIsDealstackSupport();

	// Per-signer "3 dots" menu labels. These toggles affect only the individual
	// signer, whereas the group header control applies to the whole role.
	const signerMenuLabels = {
		enableSimpleSign: tMipManage('documents_page.signer.menu.enable_simple_sign'),
		disableSimpleSign: tMipManage('documents_page.signer.menu.disable_simple_sign'),
		enableWitnessRequirement: tMipManage(
			'documents_page.signer.menu.enable_witness_requirement'
		),
		disableWitnessRequirement: tMipManage(
			'documents_page.signer.menu.disable_witness_requirement'
		),
	};

	const toggleSimpleSignForSigner = (
		signerId: string | undefined,
		effectiveSecurityLevel: DocumentSigningSecurityLevel
	) => {
		if (!signerId) {
			return;
		}
		setSignerSecurityLevelMutation.mutate({
			signerId,
			securityLevel:
				effectiveSecurityLevel === DocumentSigningSecurityLevel.BASIC
					? DocumentSigningSecurityLevel.ADVANCED
					: DocumentSigningSecurityLevel.BASIC,
		});
	};

	const toggleWitnessForSigner = (signerId: string | undefined, currentlyRequired: boolean) => {
		if (!signerId) {
			return;
		}
		setSignerWitnessMutation.mutate({
			signerId,
			requiresWitness: !currentlyRequired,
		});
	};

	const buildRemoveMenuItem = (
		signer: RemovalTargetSigner,
		roleType: DocumentSigningRoleType
	): GroupMenuItem | undefined => {
		const signerId = signer.id;
		// The target plus every dependant the cascade would take with it — a party carries its
		// investor's PIV-side slots out, and each of those carries its own witnesses.
		const cascadeSlots = [
			signer,
			...(roleType === DocumentSigningRoleType.PARTY
				? [signer.piv, signer.representative].filter((slot) => slot !== undefined)
				: []),
		];
		const cascadeStatuses = [
			...cascadeSlots.map((slot) => slot.status),
			...cascadeSlots.flatMap((slot) =>
				(slot.witnesses ?? []).map((witness) => witness.status)
			),
		];
		const removal = describeRemoval({ signer, roleType });

		if (
			!signerId ||
			!canRemoveSigner({
				signerId,
				roleType,
				workflowStatus,
				signatureType: documentSignatureType ?? null,
				eSignProvider: documentESignProvider ?? null,
				cascadeStatuses,
				remainingSignerCount: documentSignerCount - removal.slotCount,
				remainingPartyCount:
					documentPartyCount - (roleType === DocumentSigningRoleType.PARTY ? 1 : 0),
				hasOutstandingSignatures,
			})
		) {
			return undefined;
		}

		return {
			key: 'remove-signature-requirement',
			label: tMipManage('documents_page.signer.menu.remove_signature'),
			onClick: () => onRemoveSigner({ signerId, roleType, removedNames: removal.names }),
			testId: TEST_ELEMENT_IDS.DOCUMENT_SIGNER_REMOVE,
		};
	};

	const buildMenuItemsForWitness = (
		witness: InvestmentProgrammeDocumentDetailsWitnessRes
	): GroupMenuItem[] => {
		const removeItem = buildRemoveMenuItem(
			{ id: witness.signerId, email: witness.email, status: witness.status },
			DocumentSigningRoleType.WITNESS
		);
		return removeItem ? [removeItem] : [];
	};

	const buildMenuItemsForSigner = (
		signer: SignerWithRepresentative,
		roleType: DocumentSigningRoleType = group.groupType
	): GroupMenuItem[] =>
		buildSignerMenuItems({
			removeItem: buildRemoveMenuItem(signer, roleType),
			signerId: signer.id,
			isDealstackSupport,
			simpleSignAllowed: signer.effectiveSecurityLevel === DocumentSigningSecurityLevel.BASIC,
			witnessRequired: signer.requiresWitness,
			signerStatus: signer.status,
			documentFullySigned,
			isWitnessGroup,
			documentSignatureType,
			eSignInitiated,
			securityLevelPending:
				setSignerSecurityLevelMutation.isPending &&
				setSignerSecurityLevelMutation.variables?.signerId === signer.id,
			witnessPending:
				setSignerWitnessMutation.isPending &&
				setSignerWitnessMutation.variables?.signerId === signer.id,
			witnessRequirementLock,
			onToggleSimpleSign: () =>
				toggleSimpleSignForSigner(signer.id, signer.effectiveSecurityLevel),
			onToggleWitness: () => toggleWitnessForSigner(signer.id, signer.requiresWitness),
			labels: signerMenuLabels,
		});

	const isCounterpartyGroup = group.groupType === DocumentSigningRoleType.COUNTERPARTY;

	// An authorised signatory invited by email: a counterparty signer carrying an
	// email but no DealStack user account.
	const isExternallyInvitedSigner = (signer: SignerWithRepresentative) =>
		isCounterpartyGroup && !signer.assigned && !!signer.invitedExternally;

	const visibleSigners = group.signers.filter(
		(signer) =>
			signer.assigned ||
			signer.status === DocumentWorkflowInstanceSignatureStatus.COMPLETED ||
			isExternallyInvitedSigner(signer)
	);

	// Unassigned, not-yet-invited counterparty slots that an authorised signatory can be
	// invited into. Maps positionally onto the rendered empty slots below.
	const invitableSigners = isCounterpartyGroup
		? group.signers.filter(
				(signer) =>
					!signer.assigned &&
					!signer.invitedExternally &&
					!isConcludedStatus(signer.status) &&
					!!signer.signatureId
			)
		: [];

	const filledCount = visibleSigners.length;
	const totalSlots = isCounterpartyGroup ? group.requiredNumberOfSignatures ?? 0 : 0;
	const emptySlotCount = isCounterpartyGroup ? Math.max(0, totalSlots - filledCount) : 0;

	// The assignment limit is reached once every counterparty slot is filled (no empty
	// slots remain). Assigning beyond this errors, so the assign action must be hidden here.
	const allSlotsAssigned = isCounterpartyGroup && totalSlots > 0 && emptySlotCount === 0;

	const canAssign = isCounterpartyGroup && !!group.roleId && !allSlotsAssigned;

	// A committed signature can't be undone, so Unassign is only offered while at least one
	// assigned counterparty is still unsigned — otherwise there's nothing left to unassign
	// (even if empty slots remain) and the unassign form's options list would be empty.
	const hasUnassignableSigner = group.signers.some(
		(s) => s.assigned && !isConcludedStatus(s.status)
	);
	const canUnassign = isCounterpartyGroup && !!group.roleId && hasUnassignableSigner;

	const unassignUserOptions: UserOption[] = group.signers
		.filter(
			(signer): signer is SignerWithRepresentative & { userId: string } =>
				!!signer.status &&
				!isConcludedStatus(signer.status) &&
				signer.assigned &&
				typeof signer.userId === 'string'
		)
		.map((signer) => ({
			value: signer.userId,
			label: `${signer.firstName ?? ''} ${signer.lastName ?? ''}`.trim() || 'Unknown',
			email: signer.email ?? undefined,
		}));

	const signDisabled = (isCounterpartyGroup && isCounterpartySigningBlocked) || eSignInitiated;
	const signDisabledMessage = signDisabled
		? tMipManage('documents_page.signer.sign_disabled_message.awaiting_document_generation')
		: undefined;

	// Inviting is gated on readiness alone — `eSignInitiated` is a signing concern.
	const inviteDisabled = isCounterpartyGroup && isCounterpartySigningBlocked;
	const inviteDisabledReason = inviteDisabled
		? tMipManage(
				'documents_page.signer.empty_slot.invite_disabled_message.awaiting_document_generation'
			)
		: undefined;

	// A visual group spans multiple signing roles: the PARTY signers plus each investor's
	// nested PIV / Representative signers. The group-header toggle must reflect and act on ALL
	// of them, so aggregate over the flattened set and fan the mutation out across every role.
	const groupSigners = flattenGroupSigners(group.signers);
	const groupRoleIds = collectGroupRoleIds(group);

	// Group-level witness state: true if any signer in the group currently requires witness.
	// The toggle menu item flips to the opposite for all signers via the bulk-by-role endpoint.
	const groupRequiresWitness = groupSigners.some((signer) => signer.requiresWitness);

	// Mirrors the per-signer gate, and for the same reason: the signatures pages are laid out
	// once for the whole document, so whether a witness row still fits is a property of the
	// document rather than of this group's signers.
	const enablingWitnessBlocked = isEnablingWitnessBlocked({
		currentlyRequired: groupRequiresWitness,
		witnessRequirementLock,
	});

	// Group is "simple sign allowed" if ANY signer in the group has effective BASIC.
	const groupSimpleSignAllowed = groupSigners.some(
		(signer) => signer.effectiveSecurityLevel === DocumentSigningSecurityLevel.BASIC
	);

	// Hide once every signature is settled — SIGNED or COMPLETED — but NOT merely on
	// WAITING_FOR_WITNESS_SIGNATURES, since an admin may still disable witness to unblock.
	// Strict COMPLETED is wrong: nothing writes it any more, so a finished signature left a
	// finished group still offering both toggles. `status?.tone` can read "Complete" while a
	// child still WAITS, so it's unreliable here. Also hide once the whole workflow is
	// COMPLETED, which covers an unsigned optional slot (e.g. a 2-of-3 counterparty role)
	// that never settles itself.
	const allSignersFullyComplete =
		documentFullySigned ||
		(groupSigners.length > 0 && groupSigners.every((signer) => isSettledStatus(signer.status)));

	const renderAssignForm = (slotIndex: number) => {
		if (assigningSlotIndex !== slotIndex || !group.roleId) {
			return null;
		}
		return (
			<AssignSignerForm
				options={assignUserOptions}
				isLoading={isLoadingUsers}
				isPending={isAssignmentPending}
				onConfirm={onAssignConfirm}
				onCancel={onAssignCancel}
				selectTestId={TEST_ELEMENT_IDS.DOCUMENT_SIGNER_ASSIGN_FORM_SELECT}
				confirmTestId={TEST_ELEMENT_IDS.DOCUMENT_SIGNER_ASSIGN_FORM_CONFIRM_BUTTON}
				cancelTestId={TEST_ELEMENT_IDS.DOCUMENT_SIGNER_ASSIGN_FORM_CANCEL_BUTTON}
			/>
		);
	};

	return (
		<Flex
			direction="column"
			gap="0.25rem"
			key={group.roleId}
			data-testid={`${TEST_ELEMENT_IDS.DOCUMENT_SIGNER_GROUP_SECTION}-${group.groupType}`}
		>
			<SignerGroupHeader
				groupName={group.groupName}
				groupType={group.groupType}
				signerCount={group.signers.length}
				requiredNumberOfSignatures={group.requiredNumberOfSignatures}
				status={status}
				// Targets the first empty slot — hardcoding 0 broke this once a role could have
				// more than one slot and an earlier one got filled first (DC-1203).
				onAssignClick={canAssign ? () => onAssignClick(filledCount) : undefined}
				onUnassignClick={canUnassign ? onUnassignClick : undefined}
				witnessCurrentlyRequired={groupRequiresWitness}
				onToggleWitnessRequired={
					canToggleWitness({
						isWitnessGroup,
						eSignInitiated,
						pending: bulkSetWitnessMutation.isPending,
						fullyComplete: allSignersFullyComplete,
						enablingWitnessBlocked,
						witnessSupported: witnessSignersSupported(documentSignatureType),
						witnessRequired: groupRequiresWitness,
					}) && groupRoleIds.length > 0
						? () => {
								bulkSetWitnessMutation.mutate({
									roleIds: groupRoleIds,
									requiresWitness: !groupRequiresWitness,
								});
							}
						: undefined
				}
				simpleSignCurrentlyAllowed={groupSimpleSignAllowed}
				onToggleSimpleSignAllowed={
					canToggleSimpleSign({
						isDealstackSupport,
						isWitnessGroup,
						eSignInitiated,
						pending: bulkSetSecurityLevelMutation.isPending,
						fullyComplete: allSignersFullyComplete,
					}) && groupRoleIds.length > 0
						? () => {
								bulkSetSecurityLevelMutation.mutate({
									roleIds: groupRoleIds,
									securityLevel: groupSimpleSignAllowed
										? DocumentSigningSecurityLevel.ADVANCED
										: DocumentSigningSecurityLevel.BASIC,
								});
							}
						: undefined
				}
			/>

			{isUnassignmentGroup && group.roleId && (
				<AssignSignerForm
					options={unassignUserOptions}
					isLoading={false}
					isPending={isUnassignmentPending}
					onConfirm={onUnassignConfirm}
					onCancel={onUnassignCancel}
					placeholder={unassignFormLabels.placeholder}
					confirmLabel={unassignFormLabels.confirmLabel}
					nothingFoundMessage={unassignFormLabels.nothingFoundMessage}
				/>
			)}

			{visibleSigners.map((signer, index) => {
				const isCurrentUserSigner =
					!!currentUserId && signer.userId === currentUserId && signer.assigned;

				const isSignerReadyToSign = getIsSignerReadyToSign({
					documentSignatureType,
					documentSigningScope,
					signer,
					isSignerUpdatePending,
					isEidEasyDocument,
				});

				const parentClaimedByUser =
					group.groupType === DocumentSigningRoleType.PARTY &&
					isClaimedByCurrentUser(signer, currentUserId ?? undefined);

				const partyClaimGate = getClaimedSignGate(
					parentClaimedByUser,
					signer.status,
					tMipManage
				);

				const isInvitedAuthorisedSignatory = isExternallyInvitedSigner(signer);
				const invitedSignatureId = signer.signatureId;

				const cancelSignatoryMenuItems: GroupMenuItem[] =
					onCancelAuthorisedSignatory &&
					canCancelAuthorisedSignatory({
						isInvitedAuthorisedSignatory,
						signatureId: invitedSignatureId,
						status: signer.status,
					}) &&
					invitedSignatureId
						? [
								{
									key: 'cancel-authorised-signatory',
									label: tMipManage(
										'documents_page.signer.menu.cancel_authorised_signatory'
									),
									onClick: () =>
										onCancelAuthorisedSignatory(
											invitedSignatureId,
											`${signer.firstName ?? ''} ${signer.lastName ?? ''}`.trim() ||
												signer.email ||
												''
										),
									testId: TEST_ELEMENT_IDS.DOCUMENT_SIGNER_CANCEL_AUTHORISED_SIGNATORY,
								},
							]
						: [];

				const { showSignButton, showResendButton } = getSignerActionGates({
					isCounterpartyGroup,
					isCurrentUserSigner,
					isSignerReadyToSign,
					hasSignHandler: !!onSignClick,
					status: signer.status,
					parentClaimedByUser,
					hasClaimedSignHandler: !!onSignClaimedSigner,
					isInvitedAuthorisedSignatory,
					signatureId: invitedSignatureId,
					hasResendHandler: !!onResendAuthorisedSignatoryInvitation,
				});
				const handleResendInvitationClick =
					showResendButton && invitedSignatureId
						? () => onResendAuthorisedSignatoryInvitation?.(invitedSignatureId)
						: undefined;

				const { showWitnessButton, hasExistingWitness } = getWitnessControlsForSigner({
					signer,
					groupType: group.groupType,
					documentSignatureType,
					onInviteWitness,
				});
				const witnessButtonLabel = hasExistingWitness
					? tMipManage('documents_page.signer.update_witness_button')
					: tMipManage('documents_page.signer.email_witness_button');

				const signerKey =
					signer.userId ?? signer.email ?? `${group.roleId}-signer-${index}`;

				const hasPivOrRep = !!signer.representative || !!signer.piv;
				const signerTags = [
					...(signer.piv
						? [
								{
									label: tMipManage('documents_page.signer.tags.piv'),
									icon: 'link-chain',
								},
							]
						: []),
					...(signer.representative
						? [
								{
									label: tMipManage('documents_page.signer.tags.representative'),
									icon: 'link-chain',
								},
							]
						: []),
				];

				const rep = signer.representative;
				const pivSigner = signer.piv;

				const pivClaimedByUser = pivSigner
					? isClaimedByCurrentUser(pivSigner, currentUserId ?? undefined)
					: false;
				const repClaimedByUser = rep
					? isClaimedByCurrentUser(rep, currentUserId ?? undefined)
					: false;

				const pivClaimGate = getClaimedSignGate(
					pivClaimedByUser,
					pivSigner?.status,
					tMipManage
				);
				const repClaimGate = getClaimedSignGate(repClaimedByUser, rep?.status, tMipManage);

				const handleWitnessClick =
					showWitnessButton && signer.signatureId
						? () => {
								const signatureId = signer.signatureId;
								if (signatureId && onInviteWitness) {
									onInviteWitness(signatureId);
								}
							}
						: undefined;

				const isNonWitnessGroup = group.groupType !== DocumentSigningRoleType.WITNESS;
				const resolvedResendWitnessInvitation = isNonWitnessGroup
					? onResendWitnessInvitation
					: undefined;

				const claimMenuItems = buildClaimMenuItems(signer);

				const pivWitnessControls = pivSigner
					? getWitnessControlsForSigner({
							signer: pivSigner,
							groupType: group.groupType,
							documentSignatureType,
							onInviteWitness,
						})
					: null;
				const handlePivWitnessClick =
					pivWitnessControls?.showWitnessButton && pivSigner?.signatureId
						? () => {
								const pivSigId = pivSigner.signatureId;
								if (pivSigId && onInviteWitness) {
									onInviteWitness(pivSigId);
								}
							}
						: undefined;
				const pivWitnessButtonLabel = pivWitnessControls?.hasExistingWitness
					? tMipManage('documents_page.signer.update_witness_button')
					: tMipManage('documents_page.signer.email_witness_button');

				const repWitnessControls = rep
					? getWitnessControlsForSigner({
							signer: rep,
							groupType: group.groupType,
							documentSignatureType,
							onInviteWitness,
						})
					: null;
				const handleRepWitnessClick =
					repWitnessControls?.showWitnessButton && rep?.signatureId
						? () => {
								const repSigId = rep.signatureId;
								if (repSigId && onInviteWitness) {
									onInviteWitness(repSigId);
								}
							}
						: undefined;
				const repWitnessButtonLabel = repWitnessControls?.hasExistingWitness
					? tMipManage('documents_page.signer.update_witness_button')
					: tMipManage('documents_page.signer.email_witness_button');

				return (
					<SignerCard
						key={signerKey}
						firstName={signer.firstName}
						lastName={signer.lastName}
						email={signer.email}
						status={getDisplaySignerStatus(signer, showWitnessButton)}
						tags={signerTags.length > 0 ? signerTags : undefined}
						subtitle={
							isInvitedAuthorisedSignatory
								? tMipManage('documents_page.signer.subtitle.authorised_signatory')
								: undefined
						}
						witnesses={signer.witnesses}
						showSignButton={showSignButton}
						signDisabled={
							parentClaimedByUser ? partyClaimGate.notYetGenerated : signDisabled
						}
						signDisabledMessage={
							parentClaimedByUser
								? partyClaimGate.disabledMessage
								: signDisabledMessage
						}
						onSignClick={
							parentClaimedByUser
								? () => onSignClaimedSigner?.(signer, group.groupType)
								: onSignClick
						}
						showWitnessButton={showWitnessButton}
						witnessButtonLabel={witnessButtonLabel}
						onWitnessClick={handleWitnessClick}
						showResendButton={showResendButton}
						resendButtonLabel={tMipManage(
							'documents_page.signer.resend_witness_button'
						)}
						onResendClick={handleResendInvitationClick}
						isResending={resendingSignatureId === invitedSignatureId}
						onResendWitnessInvitation={resolvedResendWitnessInvitation}
						resendingSignatureId={resendingSignatureId}
						claimMenuItems={claimMenuItems}
						claimedByName={signer.claimedByName}
						defaultExpanded={(signer.witnesses?.length ?? 0) > 0 || hasPivOrRep}
						menuItems={[
							...buildMenuItemsForSigner(signer),
							...cancelSignatoryMenuItems,
						]}
						menuTestId={`${TEST_ELEMENT_IDS.DOCUMENT_SIGNER_MENU_TRIGGER}-${group.groupType}`}
						getWitnessMenuItems={buildMenuItemsForWitness}
					>
						{hasPivOrRep && (
							<>
								{pivSigner && (
									<SignerCard
										firstName={pivSigner.firstName}
										lastName={pivSigner.lastName}
										email={pivSigner.email}
										status={getDisplaySignerStatus(
											pivSigner,
											pivWitnessControls?.showWitnessButton ?? false
										)}
										subtitle={tMipManage('documents_page.signer.subtitle.piv')}
										witnesses={pivSigner.witnesses}
										showSignButton={pivClaimedByUser && !!onSignClaimedSigner}
										signDisabled={pivClaimGate.notYetGenerated}
										signDisabledMessage={pivClaimGate.disabledMessage}
										onSignClick={
											pivClaimedByUser
												? () =>
														onSignClaimedSigner?.(
															pivSigner,
															DocumentSigningRoleType.PIV
														)
												: undefined
										}
										showWitnessButton={pivWitnessControls?.showWitnessButton}
										witnessButtonLabel={pivWitnessButtonLabel}
										onWitnessClick={handlePivWitnessClick}
										onResendWitnessInvitation={resolvedResendWitnessInvitation}
										resendingSignatureId={resendingSignatureId}
										claimMenuItems={buildClaimMenuItems(pivSigner)}
										claimedByName={pivSigner.claimedByName}
										defaultExpanded={(pivSigner.witnesses?.length ?? 0) > 0}
										menuItems={buildMenuItemsForSigner(
											pivSigner,
											DocumentSigningRoleType.PIV
										)}
										menuTestId={`${TEST_ELEMENT_IDS.DOCUMENT_SIGNER_MENU_TRIGGER}-${DocumentSigningRoleType.PIV}`}
										getWitnessMenuItems={buildMenuItemsForWitness}
									/>
								)}
								{rep && (
									<SignerCard
										firstName={rep.firstName}
										lastName={rep.lastName}
										email={rep.email}
										status={getDisplaySignerStatus(
											rep,
											repWitnessControls?.showWitnessButton ?? false
										)}
										subtitle={tMipManage(
											'documents_page.signer.subtitle.legal_representative'
										)}
										witnesses={rep.witnesses}
										showSignButton={repClaimedByUser && !!onSignClaimedSigner}
										signDisabled={repClaimGate.notYetGenerated}
										signDisabledMessage={repClaimGate.disabledMessage}
										onSignClick={
											repClaimedByUser
												? () =>
														onSignClaimedSigner?.(
															rep,
															DocumentSigningRoleType.REPRESENTATIVE
														)
												: undefined
										}
										showWitnessButton={repWitnessControls?.showWitnessButton}
										witnessButtonLabel={repWitnessButtonLabel}
										onWitnessClick={handleRepWitnessClick}
										onResendWitnessInvitation={resolvedResendWitnessInvitation}
										resendingSignatureId={resendingSignatureId}
										claimMenuItems={buildClaimMenuItems(rep)}
										claimedByName={rep.claimedByName}
										defaultExpanded={(rep.witnesses?.length ?? 0) > 0}
										menuItems={buildMenuItemsForSigner(
											rep,
											DocumentSigningRoleType.REPRESENTATIVE
										)}
										menuTestId={`${TEST_ELEMENT_IDS.DOCUMENT_SIGNER_MENU_TRIGGER}-${DocumentSigningRoleType.REPRESENTATIVE}`}
										getWitnessMenuItems={buildMenuItemsForWitness}
									/>
								)}
							</>
						)}
					</SignerCard>
				);
			})}

			{Array.from({ length: emptySlotCount }, (_, i) => {
				const slotIndex = filledCount + i;
				const slotNumber = slotIndex + 1;
				const invitableSignatureId = invitableSigners[i]?.signatureId;
				return (
					<EmptySignerSlot
						key={`empty-slot-${slotIndex}`}
						slotNumber={slotNumber}
						totalSlots={totalSlots}
						onClaim={currentUserCanClaim ? onClaim : undefined}
						onAssign={() => onAssignClick(slotIndex)}
						onInviteAuthorisedSignatory={
							invitableSignatureId && onInviteAuthorisedSignatory
								? () => onInviteAuthorisedSignatory(invitableSignatureId)
								: undefined
						}
						inviteDisabled={inviteDisabled}
						inviteDisabledReason={inviteDisabledReason}
						isClaimPending={isClaimPending}
						assignForm={renderAssignForm(slotIndex)}
					/>
				);
			})}
		</Flex>
	);
};
